import { FC, useCallback, useEffect, useState } from 'react'
import { useSearchParams } from '@/hooks/useSearchParams'
import usePagination from '@/hooks/usePagination'
import Pagination from '@UI/New/Pagination'
import CustomTable from '@common/CustomTable/Table'
import EditEntityModal from '@modals/EditEntityModal/EditEntityModal'
import { reportAPI } from '@/api'
import { IGroupLessonPaymentByLessonItem } from '../../utils/types'
import { DEFAULT_DATES } from '../../utils/constants'
import useTableColumns from './useTableColumns'
import cn from './index.module.css'
import ChangeStudentsLessonStatusModal from './modals/ChangeStudentsLessonStatusModal/ChangeStudentsLessonStatusModal'
import ViewLessonIncomeModal from './modals/ViewLessonIncomeModal/ViewLessonIncomeModal'
import { useSelector } from 'react-redux'

interface IByLessonsTable {
  studentId?: number | string | null
  startDate?: string | null
  endDate?: string | null
  status?: string | null
}

const MOCK_DATA: IGroupLessonPaymentByLessonItem[] = [
  {
    id: 1,
    index: 1,
    date_time: '2026-05-05T15:00:00',
    seminarian: {
      id: 104,
      name: 'Кузнецова Ольга Викторовна',
      avatar_path: 'https://randomuser.me/api/portraits/women/55.jpg',
    } as IUser,
    course: {
      id: 1,
      name: 'Основы богословия',
    } as ICourse,
    group: {
      id: 11,
      name: 'Группа А-101',
    } as IGroup,
    status: 5,
    duration: '90 мин',
    group_student_count: 18,
    visited_count: 16,
    missed_count: 2,
    income: 24000,
    salary: 9000,
    comment: 'Занятие проведено по плану',
  },
  {
    id: 2,
    index: 2,
    date_time: '2026-05-07T18:30:00',
    seminarian: {
      id: 105,
      name: 'Смирнов Алексей Павлович',
      avatar_path: 'https://randomuser.me/api/portraits/men/32.jpg',
    } as IUser,
    course: {
      id: 2,
      name: 'История Церкви',
    } as ICourse,
    group: {
      id: 12,
      name: 'Группа Б-202',
    } as IGroup,
    status: 10,
    duration: '90 мин',
    group_student_count: 22,
    visited_count: 0,
    missed_count: 22,
    income: 0,
    salary: 0,
    comment: 'Отмена со стороны ученика',
  },
  {
    id: 3,
    index: 3,
    date_time: '2026-05-09T11:00:00',
    seminarian: {
      id: 106,
      name: 'Орлова Марина Сергеевна',
      avatar_path: 'https://randomuser.me/api/portraits/women/44.jpg',
    } as IUser,
    course: {
      id: 3,
      name: 'Литургика',
    } as ICourse,
    group: {
      id: 13,
      name: 'Группа В-303',
    } as IGroup,
    status: 11,
    duration: '60 мин',
    group_student_count: 15,
    visited_count: 0,
    missed_count: 15,
    income: 0,
    salary: 0,
    comment: 'Педагог перенес занятие',
  },
  {
    id: 4,
    index: 4,
    date_time: '2026-05-12T16:00:00',
    seminarian: {
      id: 107,
      name: 'Федоров Илья Николаевич',
      avatar_path: 'https://randomuser.me/api/portraits/men/67.jpg',
    } as IUser,
    course: {
      id: 4,
      name: 'Догматическое богословие',
    } as ICourse,
    group: {
      id: 14,
      name: 'Группа Г-404',
    } as IGroup,
    status: 12,
    duration: '120 мин',
    group_student_count: 19,
    visited_count: 0,
    missed_count: 19,
    income: 12000,
    salary: 3000,
    comment: 'Платная отмена',
  },
]

const SORTING_COLUMNS = [
  'date-time',
  'seminarian',
  'course',
  'group',
  'status',
  'duration',
  'group-student-count',
  'visited-count',
  'missed-count',
  'income',
  'salary',
  'profit',
]

const ByLessonsTable: FC<IByLessonsTable> = ({
  studentId,
  startDate,
  endDate,
  status,
}) => {
  const role = useSelector((state: any) => state.profile.profile?.role)
  const [startDateFromSearch] = useSearchParams('start-date', DEFAULT_DATES.startDate)
  const [endDateFromSearch] = useSearchParams('end-date', DEFAULT_DATES.endDate)
  const [directionId] = useSearchParams('direction-id')
  const [groupId] = useSearchParams('group-id')
  const [search] = useSearchParams('all-search')
  const [franchiseIds] = useSearchParams('franchise-ids')
  const effectiveStartDate = startDate ?? startDateFromSearch ?? DEFAULT_DATES.startDate
  const effectiveEndDate = endDate ?? endDateFromSearch ?? DEFAULT_DATES.endDate
  const [userModal, setUserModal] = useState<IUser | null>(null)
  const [lessonStatusModal, setLessonStatusModal] =
    useState<IGroupLessonPaymentByLessonItem | null>(null)
  const [lessonIncomeModal, setLessonIncomeModal] =
    useState<IGroupLessonPaymentByLessonItem | null>(null)
  const [sorting, setSorting] = useState<
    { key: string; order: 'asc' | 'desc' | 'none' }[]
  >(SORTING_COLUMNS.map((item) => ({ key: item, order: 'none' })))

  const onSortingClick = (key: string) => {
    setSorting((prev) =>
      prev.map((item) => {
        const order =
          key === item.key
            ? item.order === 'none'
              ? 'asc'
              : item.order === 'asc'
              ? 'desc'
              : 'none'
            : 'none'

        return { ...item, order }
      }),
    )
  }

  const getFilters = () => {
    const filterParams: Record<string, string> = {}
    const sort = sorting.find((item) => item.order !== 'none')

    if (sort) {
      filterParams['sort_order'] = sort.order
      filterParams['sort_key'] = sort.key
    }

    if (effectiveStartDate) {
      filterParams['start_date'] = effectiveStartDate
    }

    if (effectiveEndDate) {
      filterParams['end_date'] = effectiveEndDate
    }

    if (directionId) {
      filterParams['direction_id'] = directionId
    }

    if (groupId) {
      filterParams['group_id'] = groupId
    }

    if (studentId) {
      filterParams['student_id'] = String(studentId)
    }

    if (status && status !== 'all') {
      filterParams['status'] = String(status)
    }

    if (search) {
      filterParams['search'] = search
    }
    if (franchiseIds) filterParams['franchise_ids'] = franchiseIds

    return filterParams
  }

  const {
    state,
    loading,
    getState: getData,
    ...params
  } = usePagination({
    api: reportAPI.groupLessonPayment.byLessons,
    params: { ...getFilters() },
    firstRequest: false,
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      getData()
      params.setCurrentPage(1)
    }, 250)

    return () => {
      clearTimeout(handler)
    }
  }, [
    effectiveStartDate,
    effectiveEndDate,
    directionId,
    groupId,
    studentId,
    status,
    search,
    sorting,
    role,
    franchiseIds,
  ])

  const data = state as IGroupLessonPaymentByLessonItem[]

  const onChangeStatusClick = useCallback(
    (lesson: IGroupLessonPaymentByLessonItem) => {
      setLessonStatusModal(lesson)
    },
    [],
  )

  const onIncomeClick = useCallback(
    (lesson: IGroupLessonPaymentByLessonItem) => {
      setLessonIncomeModal(lesson)
    },
    [],
  )

  const columns = useTableColumns({
    onSeminarianClick: (seminarian) => setUserModal(seminarian),
    onChangeStatusClick,
    onIncomeClick,
    showStudentStatus: Boolean(studentId),
  })

  return (
    <>
      <div className={cn.content}>
        <CustomTable<IGroupLessonPaymentByLessonItem>
          flexColumns
          data={data}
          columns={columns}
          loading={loading}
          sorting={sorting}
          tooltips={
            studentId
              ? [
                  {
                    id: 'student-status',
                    tooltip: 'Статус выбранного учащегося на этом занятии',
                  },
                ]
              : undefined
          }
          onSortingClick={onSortingClick}
        />
        <Pagination {...{ ...params }} />
      </div>
      {!!userModal && (
        <EditEntityModal
          type="employee"
          open={!!userModal}
          employeeId={userModal?.id ?? null}
          onDismiss={() => setUserModal(null)}
          onUpdate={() => setUserModal(null)}
        />
      )}
      <ChangeStudentsLessonStatusModal
        open={!!lessonStatusModal}
        lesson={lessonStatusModal}
        onDismiss={() => setLessonStatusModal(null)}
        onSuccess={() => {
          setLessonStatusModal(null)
          getData()
        }}
      />
      <ViewLessonIncomeModal
        open={!!lessonIncomeModal}
        lesson={lessonIncomeModal}
        onDismiss={() => setLessonIncomeModal(null)}
      />
    </>
  )
}

export default ByLessonsTable
